// =============================
// Imports
// =============================

// External Dependencies
import { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Field, Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { Icons } from '@mewo/components';
import Router from 'next/router';
import catalogplaceholder from '@mewo/components/assets/images/placeholders/catalog_500x500.png?webp';
import _get from 'lodash/get';

// Config
import { withTranslation } from '../../../../config/i18n';
import entityConfig from '../../meta/catalogs/config';

// Actions
import { panelActions, triggerAutosaveCheck } from '../../../../store/actions/SidePanelActions';

// Constants
import * as spts from '../../../../store/constants/SidePanelTypes';

// Components
import BaseSidePanel from '../../../faac/panels/base';
import DocumentStatus from '../documentStatus';
import Autosave from '../../../other/autosave';
import MewoInput from '../../../presentationals/mewoInput';
import Input from '../../../presentationals/inputs/input';
import MetaDownloadDropdown from '../../../other/metaDownloadDropdown';
import InputWithLanguage from '../../../containers/inputWithLanguage';
import ApiSelectInput from '../../../containers/inputs/apiSelectInput';
import TextAreaInput from '../../../presentationals/inputs/textAreaInput';
import WithMetaSearchRoute from '../../../faac/withMetaSearchRoute';
import MetaRightsInformations from '../metaRightsInformations';
import ContactsInput from '../../../containers/contactsInput';
import Uploader from '../../../presentationals/uploader';
import MetaCustomFields from '../metaCustomFields';
import CopyLink from '../copyLink';
import Autotag from '../autotag';

import validator from '../../../../validators/meta/catalog';

// Helpers
import * as pth from '../../../../helpers/proptypes';
import { getSiteOrigin } from '../../../../helpers/misc';
import { getApiSelectInputData, getDocumentRights, getGenericDocCoverUrl } from '../../../../helpers/meta-common';
import { formatDateString } from '../../../../helpers/dates';
import { getDocName, getSearchName } from '../../../../helpers/doc-names';

// Styles
import {
  DocumentBody,
  DocumentCreatedAt,
  DocumentName,
  DocumentType,
  DownloadActionIcon,
  DownloadActionWrapper,
  FileHelper,
  GenericCover,
  GenericOwnership,
  GenericOwnershipCount,
  GenericSearchIcon,
  GenericSearchLink,
  LeftColumn,
  LineInnerLoader,
  LineLoader,
  LineLoaderWrapper,
  MiscHelper,
  RelationJobTooltip,
  RelationsWrapper,
  RelationTitleWrapper,
  RightColumn,
  SectionBox,
  SectionNotice,
  SectionTitle,
  TabElement,
  TabLink,
  TabLinksWrapper,
} from '../common.styles';
import { InputWrapper } from '../../global.styles';

// =============================
// Component
// =============================

const entity = 'catalog';

class MetaCatalog extends Component {
  static propTypes = {
    panel: PropTypes.shape({
      data: pth.catalog,
      additionalData: PropTypes.shape({
        tracks: PropTypes.shape({
          data: PropTypes.number,
        }),
        shares: PropTypes.shape({
          data: PropTypes.shape({
            public: PropTypes.number,
            agents: PropTypes.objectOf(PropTypes.number),
            showcase: PropTypes.objectOf(PropTypes.number),
          }),
        }),
        autotagging: PropTypes.shape({
          data: PropTypes.shape({
            nb_tracks: PropTypes.number,
            nb_tracks_not_autotagged: PropTypes.number,
          }),
        }),
      }),
      isLoading: PropTypes.bool.isRequired,
      isModifying: PropTypes.bool.isRequired,
      'isModifying-shares': PropTypes.bool,
      isAutotagging: PropTypes.bool.isRequired,
      isDuplicating: PropTypes.bool.isRequired,
      isUploading: PropTypes.bool.isRequired,
      isDeletingUpload: PropTypes.bool.isRequired,
      isDeleting: PropTypes.bool.isRequired,
      uploadProgress: PropTypes.number.isRequired,
    }).isRequired,
    localPreferences: pth.localPreferences.isRequired,
    relationsNotifications: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      progress: PropTypes.number,
    })),
    customFields: pth.customFieldsConfig.isRequired,
    providers: PropTypes.arrayOf(pth.provider).isRequired,
    triggerPanelAutosaveCheck: PropTypes.func.isRequired,
    i18n: PropTypes.shape({
      language: PropTypes.string,
    }).isRequired,
    t: PropTypes.func.isRequired,
  };

  static defaultProps = {
    relationsNotifications: [],
  };

  componentDidUpdate(prevProps) {
    const { relationsNotifications: prevRelationsNotifications } = prevProps;
    const { relationsNotifications, panel: { data } } = this.props;

    if (
      data
      && typeof this.refreshPanel === 'function'
      && prevRelationsNotifications.length
      && !relationsNotifications.length
    ) {
      this.refreshPanel(true);
    }
  }

  pickFormData = (data, documentRights, init = false) => {
    if (!documentRights.canWrite && !init) {
      return {
        showcase: _get(data, 'showcase', []),
      };
    }

    const keys = {
      name: _get(data, 'name', ''),
      showcase: init
        ? _get(data, 'showcase', []).map(({ id }) => id)
        : _get(data, 'showcase', []),
      descriptions: _get(data, 'descriptions', [])
        .map(({ locale, value }) => ({ locale, value })),
    };

    if (documentRights.canAccessNotes) {
      keys.notes = _get(data, 'notes', '');
    }

    if (documentRights.isOwner) {
      keys.public = _get(data, 'public', false);
      keys.agents = _get(data, 'agents', []);
      keys.default_label = getApiSelectInputData(data.default_label, 'label', init);
      keys.default_publisher = getApiSelectInputData(data.default_publisher, 'publisher', init);

      keys.contacts = _get(data, 'contacts', [])
        .filter(people => (init ? true : !!people.people.value))
        .map((people) => {
          if (init) {
            return {
              people: getApiSelectInputData(people, 'people', init),
              role: people.role,
            };
          }

          return {
            people: getApiSelectInputData(people.people, 'people', init),
            role: people.role,
          };
        });

      keys.custom = _get(data, 'custom', {});
    }

    return keys;
  };

  renderPanelAnchors = (data) => {
    const { customFields, providers, t } = this.props;

    const documentRights = getDocumentRights(
      data.owned_by_tenant,
      data.tenant.id,
      providers,
    );

    const hasCustomFields = !!customFields.fields
      .filter(customField => customField.collections.includes(entity))
      .length;

    return (
      <TabLinksWrapper>
        <TabLink name="general_information">
          {t(`pages:meta.${entity}s.panel.general_information_anchor`)}
        </TabLink>
        {(documentRights.isOwner || !!data.contacts?.length) && (
          <TabLink name="contacts_information">
            {t(`pages:meta.${entity}s.panel.contacts_information_anchor`)}
          </TabLink>
        )}
        {(documentRights.isOwner && hasCustomFields) && (
          <TabLink name="custom_fields_information">
            {t(`pages:meta.${entity}s.panel.custom_fields_information_anchor`)}
          </TabLink>
        )}
        {documentRights.isOwner && (
          <TabLink name="agents_information">
            {t(`pages:meta.${entity}s.panel.agents_information_anchor`)}
          </TabLink>
        )}
        {!documentRights.isOwner && (
          <TabLink name="providers_information">
            {t(`pages:meta.${entity}s.panel.providers_information_anchor`)}
          </TabLink>
        )}
        <TabLink name="modo_information">
          {t(`pages:meta.${entity}s.panel.modo_information_anchor`)}
        </TabLink>
        <TabLink name="uploads_information">
          {t(`pages:meta.${entity}s.panel.uploads_information_anchor`)}
        </TabLink>
      </TabLinksWrapper>
    );
  };

  render() {
    const {
      panel,
      localPreferences,
      relationsNotifications,
      customFields,
      providers,
      triggerPanelAutosaveCheck,
      i18n: { language },
      t,
    } = this.props;

    const {
      data,
      additionalData,
      isModifying,
      isAutotagging,
      isDuplicating,
      isUploading,
      isDeletingUpload,
      isDeleting,
      uploadProgress,
    } = panel;

    const hasCustomFields = !!customFields.fields
      .filter(customField => customField.collections.includes(entity))
      .length;

    const shareLink = _get(data, 'id')
      ? `${getSiteOrigin()}/meta/${entity}s?panel=${data.id}`
      : '';

    return (
      <BaseSidePanel
        {...this.props}
        get={panelActions[spts.META_CATALOG_PANEL].get}
        getAdditionalOptions={{
          fnc: panelActions[spts.META_CATALOG_PANEL].getAdditional,
          list: [
            ['count/tracks', 'tracks'],
            ['count/shares', 'shares'],
            [
              `/meta/autotagging/${entity}/${panel.id}/data`,
              'autotagging',
              '',
              panelData => panelData?.owned_by_tenant,
            ],
          ],
        }}
        modify={panelActions[spts.META_CATALOG_PANEL].modify}
        modifyAdditional={panelActions[spts.META_CATALOG_PANEL].modifyAdditional}
        autotagOptions={{
          fnc: panelActions[spts.META_CATALOG_PANEL].autotag,
          getAdditionalList: [
            [
              `/meta/autotagging/${entity}/${panel.id}/data`,
              'autotagging',
            ],
          ],
        }}
        duplicate={panelActions[spts.META_CATALOG_PANEL].duplicate}
        uploadFile={panelActions[spts.META_CATALOG_PANEL].uploadFile}
        deleteFile={panelActions[spts.META_CATALOG_PANEL].deleteFile}
        del={panelActions[spts.META_CATALOG_PANEL].delete}
        lang={{
          autotagTitle: t(`pages:meta.${entity}s.autotag_documents`, { name: data?.name }),
          autotagDesc: t(`pages:meta.${entity}s.autotag_documents_notice`),
          deleteConfirmTitle: t(`pages:meta.${entity}s.delete_documents`),
          deleteConfirmDesc: t(`pages:meta.${entity}s.delete_documents_notice`),
        }}
      >
        {({
          refreshPanel,
          modifyDocument,
          modifyAdditional,
          autotagDocument,
          duplicateDocument,
          uploadDocumentFile,
          deleteDocumentFile,
          deleteDocument,
        }) => {
          if (!data) return null;

          this.refreshPanel = refreshPanel;

          const relationsNotification = relationsNotifications
            .find(notification => notification.id === data.id);

          const documentRights = getDocumentRights(
            data.owned_by_tenant,
            data.tenant.id,
            providers,
          );

          const image = _get(data, 'image.original.url')
            ? {
              name: _get(data, 'image.original.file_name'),
              url: _get(data, 'image.original.url'),
            }
            : null;

          let uploadStatus = data.has_file ? 2 : 0;
          // This needs to be last as it overwrites everything
          if (isUploading || isDeletingUpload) uploadStatus = 1;

          const formDisabled = !documentRights.canWrite
            || !!relationsNotification
            || panel['isModifying-shares']
            || isUploading
            || isDeletingUpload;
          const writableForAgentDisabled = !!relationsNotification
            || isUploading
            || isDeletingUpload;
          const uploadDisabled = !documentRights.canWrite
            || !!relationsNotification
            || isModifying;

          const getASIAction = (key, ASIEntity) => {
            if (!_get(data, key)) return null;

            return () => triggerPanelAutosaveCheck(
              'open',
              [spts.metaMapping[ASIEntity], _get(data, `${key}.id`)],
            );
          };

          return (
            <Fragment>
              <LeftColumn>
                <GenericCover
                  placeholder={catalogplaceholder}
                  cover={getGenericDocCoverUrl(data.image)}
                />
                <DocumentName>
                  {data.name}
                </DocumentName>
                <DocumentType>
                  {t(`common:entities.${entity}`)}
                </DocumentType>
                <Autotag.Status
                  autotaggingData={_get(additionalData, 'autotagging.data')}
                  documentRights={documentRights}
                  entity={entity}
                  t={t}
                />
                <DocumentCreatedAt>
                  {t(`pages:meta.${entity}s.panel.created_at`)}
                  :&nbsp;
                  {formatDateString(data.created_at, localPreferences.dateFormat, true)}
                </DocumentCreatedAt>
                <CopyLink
                  link={shareLink}
                  content={t(`pages:meta.${entity}s.panel.copy_share_link`)}
                  tooltipContent={t(`pages:meta.${entity}s.panel.copy_share_link_tooltip`)}
                />
                {!!_get(additionalData, 'tracks.data') && (
                  <GenericOwnership>
                    <GenericOwnershipCount>
                      {t('common:entities.number_tracks', {
                        count: _get(additionalData, 'tracks.data'),
                      })}
                    </GenericOwnershipCount>
                  </GenericOwnership>
                )}

                <Autotag.ColumnAction
                  autotagDocument={autotagDocument}
                  autotaggingData={_get(additionalData, 'autotagging.data')}
                  documentRights={documentRights}
                  entity={entity}
                  t={t}
                />

                <MetaDownloadDropdown
                  entity={entity}
                  documentId={data.id}
                  documentName={data.name}
                  canDownload={!!_get(additionalData, 'tracks.data')}
                  togglerElement={(
                    <DownloadActionWrapper>
                      <DownloadActionIcon />
                      {t(`pages:meta.${entity}s.downloads.toggler_title`)}
                    </DownloadActionWrapper>
                  )}
                />

                {!!_get(additionalData, 'tracks.data') && (
                  <WithMetaSearchRoute
                    entity="track"
                    filters={{ catalogs: [[data.id, data.name]] }}
                    resetQuery
                  >
                    {routeQuery => (
                      <GenericSearchLink onClick={() => Router.push(`/meta/tracks${routeQuery}`)}>
                        <GenericSearchIcon />
                        {t(`pages:meta.${entity}s.panel.search_tracks`)}
                      </GenericSearchLink>
                    )}
                  </WithMetaSearchRoute>
                )}
                <WithMetaSearchRoute
                  entity="album"
                  filters={{ catalogs: [[data.id, data.name]] }}
                  resetQuery
                >
                  {routeQuery => (
                    <GenericSearchLink onClick={() => Router.push(`/meta/albums${routeQuery}`)}>
                      <GenericSearchIcon />
                      {t(`pages:meta.${entity}s.panel.search_albums`)}
                    </GenericSearchLink>
                  )}
                </WithMetaSearchRoute>

                {!!relationsNotification && (
                  <RelationsWrapper>
                    <RelationTitleWrapper>
                      {t(`pages:meta.${entity}s.panel.ongoing_relations_job`)}
                      <RelationJobTooltip
                        content={t(`pages:meta.${entity}s.panel.ongoing_relations_job_tooltip`)}
                      >
                        <Icons.Informations />
                      </RelationJobTooltip>
                    </RelationTitleWrapper>
                    <LineLoaderWrapper>
                      <LineLoader>
                        <LineInnerLoader progress={relationsNotification.progress} />
                      </LineLoader>
                    </LineLoaderWrapper>
                  </RelationsWrapper>
                )}
              </LeftColumn>
              <RightColumn initTab="general_information">
                <DocumentStatus
                  isModifying={isModifying}
                  isAutotagging={isAutotagging}
                  isDuplicating={isDuplicating}
                  isDeleting={isDeleting}
                  localPreferences={localPreferences}
                  documentRights={documentRights}
                  updatedAt={data.updated_at}
                  duplicateDocument={duplicateDocument}
                  deleteDocument={deleteDocument}
                />
                {this.renderPanelAnchors(data, additionalData)}
                <DocumentBody>
                  <Form
                    onSubmit={() => {}}
                    initialValues={this.pickFormData(data, documentRights, true)}
                    subscription={{ values: true }}
                    keepDirtyOnReinitialize
                    mutators={{ ...arrayMutators }}
                  >
                    {({ form, values }) => (
                      <Fragment>
                        <Autosave
                          debounce={5000}
                          save={modifyDocument}
                          nullValuesTransformer={formValues => this.pickFormData(
                            formValues, documentRights, false,
                          )}
                          autosavePanelId={`${spts.META_CATALOG_PANEL}_${data.id}`}
                        />
                        <TabElement name="general_information">
                          <SectionTitle>{t(`pages:meta.${entity}s.panel.general_information`)}</SectionTitle>
                          <SectionNotice>
                            {t(`pages:meta.${entity}s.panel.general_information_notice`)}
                          </SectionNotice>
                          <SectionBox>
                            <InputWrapper>
                              <Field
                                name="name"
                                component={MewoInput}
                                use={Input}
                                type="text"
                                label={t(`pages:meta.${entity}s.panel.name`)}
                                fullWidth
                                validate={validator.name}
                                context="autosave"
                                disabled={formDisabled}
                              />
                            </InputWrapper>
                          </SectionBox>
                          <SectionBox>
                            <Field
                              name="descriptions"
                              component={MewoInput}
                              use={InputWithLanguage}
                              defaultLanguage={language}
                              label={t(`pages:meta.${entity}s.panel.descriptions`)}
                              fullWidth
                              isTranslatable
                              inputType="textarea"
                              context="autosave"
                              disabled={formDisabled}
                              height="12rem"
                            />
                          </SectionBox>
                          {documentRights.isOwner && (
                            <SectionBox>
                              <InputWrapper>
                                <Field
                                  name="default_label"
                                  component={MewoInput}
                                  use={ApiSelectInput}
                                  type="text"
                                  label={t(`pages:meta.${entity}s.panel.default_label`)}
                                  entity="meta/labels"
                                  getEntityLabel={getDocName('label')}
                                  getSearchEntityLabel={getSearchName('label')}
                                  onTheFlyEnabled
                                  onTheFlyField="label_name"
                                  itemAction={getASIAction('default_label', 'label')}
                                  fullWidth
                                  tooltipMessage={t(`pages:meta.${entity}s.panel.default_label_tooltip`)}
                                  resetBtnTriggersOnChange
                                  context="autosave"
                                  disabled={formDisabled}
                                />
                                <Field
                                  name="default_publisher"
                                  component={MewoInput}
                                  use={ApiSelectInput}
                                  type="text"
                                  label={t(`pages:meta.${entity}s.panel.default_publisher`)}
                                  entity="meta/publishers"
                                  getEntityLabel={getDocName('publisher')}
                                  getSearchEntityLabel={getSearchName('publisher')}
                                  onTheFlyEnabled
                                  onTheFlyField="publisher_name"
                                  itemAction={getASIAction('default_publisher', 'publisher')}
                                  resetBtnTriggersOnChange
                                  fullWidth
                                  tooltipMessage={t(`pages:meta.${entity}s.panel.default_publisher_tooltip`)}
                                  context="autosave"
                                  disabled={formDisabled}
                                />
                              </InputWrapper>
                            </SectionBox>
                          )}
                          {documentRights.canAccessNotes && (
                            <SectionBox>
                              <Field
                                name="notes"
                                component={MewoInput}
                                use={TextAreaInput}
                                label={t(`pages:meta.${entity}s.panel.notes`)}
                                fullWidth
                                context="autosave"
                                disabled={formDisabled}
                                height="12rem"
                              />
                            </SectionBox>
                          )}
                        </TabElement>

                        {(documentRights.isOwner || !!data.contacts?.length) && (
                          <TabElement name="contacts_information">
                            <SectionTitle>{t(`pages:meta.${entity}s.panel.contacts_information`)}</SectionTitle>
                            <SectionNotice>
                              {t(`pages:meta.${entity}s.panel.contacts_information_notice`)}
                            </SectionNotice>
                            <SectionBox>
                              <InputWrapper>
                                <ContactsInput
                                  data={data.contacts}
                                  form={form}
                                  formValues={values}
                                  name="contacts"
                                  disabled={formDisabled}
                                  documentRights={documentRights}
                                />
                              </InputWrapper>
                            </SectionBox>
                          </TabElement>
                        )}

                        {documentRights.isOwner && hasCustomFields && (
                          <TabElement name="custom_fields_information">
                            <SectionTitle>{t(`pages:meta.${entity}s.panel.custom_fields_information`)}</SectionTitle>
                            <SectionNotice>
                              {t(`pages:meta.${entity}s.panel.custom_fields_information_notice`)}
                            </SectionNotice>
                            <SectionBox>
                              <InputWrapper>
                                <MetaCustomFields entity={entity} formDisabled={formDisabled} />
                              </InputWrapper>
                            </SectionBox>
                          </TabElement>
                        )}

                        <MetaRightsInformations
                          entity={entity}
                          sharesData={{
                            ..._get(additionalData, 'shares.data', {
                              public: 0,
                              agents: {},
                              showcase: {},
                            }),
                            total: _get(additionalData, 'tracks.data', 0),
                          }}
                          docAgents={data.agents}
                          docModos={data.showcase}
                          docPublic={data.public}
                          documentRights={documentRights}
                          formDisabled={formDisabled}
                          modifyAdditional={modifyAdditional}
                          resyncable
                          writableForAgentDisabled={writableForAgentDisabled}
                        />
                      </Fragment>
                    )}
                  </Form>

                  <TabElement name="uploads_information">
                    <SectionTitle>{t(`pages:meta.${entity}s.panel.uploads_information`)}</SectionTitle>
                    <SectionNotice>
                      {t(`pages:meta.${entity}s.panel.uploads_information_notice`)}
                    </SectionNotice>
                    <SectionBox>
                      <Uploader
                        name="image"
                        value={image}
                        status={uploadStatus}
                        disabled={uploadDisabled}
                        isUploading={isUploading}
                        uploadProgress={uploadProgress}
                        onChange={(nextValue) => {
                          if (!nextValue) return deleteDocumentFile();
                          return uploadDocumentFile(nextValue);
                        }}
                        maxSize={entityConfig.panelUploads.image.maxSize}
                      />
                      <FileHelper>
                        {t(`pages:meta.${entity}s.panel.image`)}
                      </FileHelper>
                      {_get(data, 'image.error_key') && (
                        <MiscHelper mt="1.2rem" isError>
                          {t(`errors:conversion.${_get(data, 'image.error_key')}_desc`)}
                        </MiscHelper>
                      )}
                    </SectionBox>
                  </TabElement>
                </DocumentBody>
              </RightColumn>
            </Fragment>
          );
        }}
      </BaseSidePanel>
    );
  }
}

function mapStateToProps({ core, user, meta }) {
  return {
    localPreferences: core.localPreferences,
    relationsNotifications: user.relationsNotifications.catalogs,
    customFields: meta.customFields.data,
    providers: meta.providers,
  };
}

export default compose(
  connect(mapStateToProps, {
    triggerPanelAutosaveCheck: triggerAutosaveCheck,
  }),
  withTranslation(['common', 'pages', 'components', 'errors']),
)(MetaCatalog);
